<template>
  <idf-carousel
    class="associated-product-carousel"
    v-observe-visibility="{
      callback: visibilityChanged(),
      throttle: 1000,
      intersection: {
        threshold: 0.75,
      },
      once: true,
    }"
  >
    <template #title>
      <h3>{{ title }}</h3>
    </template>
    <template v-for="(product, index) in products" #[index] :key="product._id">
      <p-product-thumbnail
        :productId="product._id"
        :productSlug="product.slug"
        data-test-product-thumbnail
        v-bind="thumbnailOptions"
        @click.capture="onProductThumbnailClick({ product, index })"
      />
    </template>
    <template v-if="ctaLabel" #footer>
      <div class="associated-product-carousel__btn">
        <adn-button :href="ctaUrl" data-test-cta kind="secondary" markup="a">
          {{ ctaLabel }}
        </adn-button>
      </div>
    </template>
  </idf-carousel>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import { useAnalyticService } from '@b2ag/plugin-aladin-gtm'
import IdfCarousel from '@b2ag/design-system/src/components/IdfCarousel/IdfCarousel.vue'
import { ObserveVisibility } from 'vue3-observe-visibility'
import PProductThumbnail from '../PProductThumbnail/PProductThumbnail.vue'
import { carouselAssociatedViewed, productAssociatedClickPayload } from './helpers'
import type { Product } from './typings'
import AdnButton from '@b2ag/design-system/src/components/AdnButton/AdnButton.vue'

export default defineComponent({
  name: 'PAssociatedProductCarousel',
  directives: {
    ObserveVisibility,
  },
  props: {
    title: String,
    productId: { type: String, required: true },
    productName: { type: String, required: true },
    products: Array as PropType<Product[]>,
    maxNbVisibleElements: Number,
    thumbnailOptions: Object,
    ctaUrl: String,
    ctaLabel: String,
    id: String,
    clickEventName: String,
    clickEventActionFieldList: String,
    viewEventName: String,
  },
  components: { IdfCarousel, PProductThumbnail, AdnButton },
  setup(props) {
    const { viewEventName, productId, productName, clickEventName, clickEventActionFieldList } = props

    const onProductThumbnailClick = (receivedPayload: { product: Product; index: number }) => {
      if (clickEventName) {
        const payload = {
          product: receivedPayload.product,
          productId,
          actionFieldList: clickEventActionFieldList,
        }
        useAnalyticService().trackEvent(clickEventName, productAssociatedClickPayload(payload))
      }
    }

    const visibilityChanged = () => (isVisible) =>
      isVisible && carouselAssociatedViewed(productId, productName, viewEventName)

    return {
      onProductThumbnailClick,
      visibilityChanged,
    }
  },
})
</script>

<style lang="scss">
.associated-product-carousel {
  --idf-carousel-items-gap: 0.5rem;

  li {
    padding: var(--spacing-3) var(--spacing-2);
    width: 25rem;
  }

  &__btn {
    display: flex;
    justify-content: center;
  }
}
</style>
